import * as React from "react";
import { type CSSProperties, Fragment, memo, useRef } from "react";
import { type User } from "../../types";
import { Icon } from "../../../common/components/icon/Icon";
import { IconType } from "../../../common/components/icon/IconType";
import { userIsRegistered, userIsSalesman } from "../../../utils/roles";
import { IconSize } from "../../../common/components/icon/IconSize";
import { SimpleMenu, withSpaceInNavigationMenuStyle } from "../../../common/components/Menu/Menu";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { getMediasVariant, UserHeaderCircle } from "./HeaderCircle";
import { useTranslation, type WithTranslation } from "react-i18next";
import { MENU_POS_LEFT } from "../../../common/components/Menu/MenuPositions";
import { MenuItemNavigation, MenuSpacer } from "../../../common/components/Menu/MenuItem";
import {
  ACCOUNT_SELECTION_URL,
  MANAGE_USERS_PAGE,
  MY_ACCOUNT_ACCOUNT_SETTINGS_PAGE,
} from "../../../utils/url/staticUrls";
import { Permission, userHasPermission } from "~/utils/permissions";
import { doLogout } from "~/Header/api/doLogout";
import { PopperTooltip } from "~/common/components/Tooltip";
import classNames from "classnames";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_TABLET } from "~/utils/screenSizes";
import { entriesMenu } from "~/Header/components/MyCompanyMenu";
import BulkATCPopup from "~/BulkATCPopup/components/BulkATCPopup";
import { sendPopupOpen } from "~/feature/Analytics/ga/bulkUpload";

import "./UserNavigation.less";
import { FormControl, InputLabel } from "@mui/material";

interface HeaderGreetingProps {
  user: User;
  showEdit: boolean;
}

export const UserNavigation = memo(({ user, showEdit }: HeaderGreetingProps) => {
  const { t } = useTranslation();

  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const { val: isOpen, setTrue: openNavigation, setFalse: handleClose } = useBooleanState(false);
  const { val: isOpenBulk, setTrue: handleOpenBulk, setFalse: handleCloseBulk } = useBooleanState(false);

  const menuAnchor = menuAnchorRef.current;

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    openNavigation();
  };

  return (
    <div className={"scha__header__welcome"} ref={menuAnchorRef}>
      <PopperTooltip tooltipContent={t("myAccount.title")} placement="bottom" hideTooltip={isOpen}>
        <div className={"d-flex flex-direction-row flex-align-center"} onClick={handleOpen}>
          <LoggedInCircle user={user} className={"scha__header__avatar"} />
          <div className={"scha__header__user-container"}>
            <Icon
              className={classNames(
                "icon-margin-l flex-none scha__header__user-container__icon",
                isOpen && "scha__header__user-container__icon-open",
              )}
              type={isOpen ? IconType.ChevronUp : IconType.ChevronDown}
              size={IconSize.FONT_20}
            />
          </div>
        </div>
        <RegisteredNavigation
          anchorEl={menuAnchor}
          isOpen={isOpen}
          onClose={handleClose}
          onOpenBulk={() => {
            handleOpenBulk();
            sendPopupOpen();
          }}
          user={user}
          showEdit={showEdit}
        />
      </PopperTooltip>
      {isOpenBulk && <BulkATCPopup onClose={handleCloseBulk} />}
    </div>
  );
});

const LoggedInCircle = React.forwardRef<HTMLDivElement, { user: User; className?: string }>(
  ({ user, className }, ref) => (
    <UserHeaderCircle className={className} variant={getMediasVariant()} ref={ref}>
      {firstLetter(user.firstName) + firstLetter(user.lastName)}
    </UserHeaderCircle>
  ),
);

function firstLetter(text: string) {
  return text ? text.substring(0, 1).toUpperCase() : "";
}

const StyledMenu = withSpaceInNavigationMenuStyle(SimpleMenu);

const RegisteredNavigation = ({
  anchorEl,
  onClose,
  isOpen,
  user,
  showEdit,
  onOpenBulk,
}: {
  anchorEl: Element | null;
  onClose: () => void;
  isOpen: boolean;
  user: User;
  showEdit: boolean;
  onOpenBulk: () => void;
}) => {
  const { t } = useTranslation();

  const { displayUid, displayName, onlyName, displayAccount, displayEndCustomerAccount, displayEndCustomerUid } =
    extractDisplayLabels(user, t);
  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const visibleMenuEntries = entriesMenu.filter((item) => item.permisisonCheck());

  return (
    <Fragment>
      <StyledMenu
        open={isOpen}
        anchorEl={anchorEl}
        {...MENU_POS_LEFT}
        onClose={onClose}
        disableAutoFocusItem
        sx={{ marginTop: { phone: 0, tablet: "16px", "desktop-s": "24px" } }}
      >
        <MenuItemNavigation>
          <span className={"f-no-wrap scha__header__user-container__displayName"}>{displayName}</span>
        </MenuItemNavigation>
        <MenuSpacer fullwidth />

        {!!displayAccount && !!displayUid && (
          <AccountMenuItem
            displayAccount={displayAccount}
            displayUid={displayUid}
            label={"header.selectedAccount"}
            dataTestId={"header-selected-account"}
            onCloseMenu={onClose}
            showEdit={showEdit}
          />
        )}

        {!!displayEndCustomerAccount && !!displayEndCustomerUid && (
          <AccountMenuItem
            displayAccount={displayEndCustomerAccount}
            displayUid={displayEndCustomerUid}
            label={"header.selectedAccountEndCustomer"}
            dataTestId={"header-selected-end-customer"}
            onCloseMenu={onClose}
            showEdit={showEdit}
          />
        )}
        {!isMinTablet && (
          <>
            {visibleMenuEntries.map((item) => item.factory(t, onClose, onOpenBulk))}
            <MenuSpacer fullwidth />
          </>
        )}
        {userHasPermission(Permission.USER_MANAGEMENT) && (
          <a data-test-id={"header-btn-manage-users"} href={MANAGE_USERS_PAGE} onClick={onClose}>
            <MenuItemNavigation onClick={onClose}>{t("header.link.userAccount")}</MenuItemNavigation>
          </a>
        )}
        <a data-test-id={"register-account-my-account"} href={MY_ACCOUNT_ACCOUNT_SETTINGS_PAGE}>
          <MenuItemNavigation onClick={onClose}>{t("header.link.accountSettings")}</MenuItemNavigation>
        </a>
        <a data-test-id={"register-account-logout"} onClick={() => doLogout()}>
          <MenuItemNavigation onClick={onClose}>{t("header.link.logout")}</MenuItemNavigation>
        </a>
      </StyledMenu>
    </Fragment>
  );
};

const AccountWidth: CSSProperties = { maxWidth: "164px" };
const AccountMenuItem = ({
  displayAccount,
  displayUid,
  label,
  dataTestId,
  onCloseMenu,
  showEdit,
}: {
  displayAccount: string;
  displayUid: string;
  label: string;
  dataTestId: string;
  onCloseMenu: () => void;
  showEdit?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Fragment>
      <MenuItemNavigation
        // @ts-ignore
        component={"a"}
        href={ACCOUNT_SELECTION_URL}
        onClick={onCloseMenu}
        className={"d-flex flex-direction-column flex-align-start"}
        data-test-id={dataTestId}
      >
        <FormControl>
          <InputLabel shrink={true} variant="standard">
            {t(label)}
          </InputLabel>
          <div className={"d-flex MuiInput-formControl pt-2"}>
            <div className={"d-flex flex-direction-column"}>
              <PopperTooltip tooltipContent={displayAccount} placement="bottom-end">
                <div className={"d-flex"}>
                  <span className={"scha__ellipsis-text"} style={AccountWidth}>
                    {displayAccount}
                  </span>
                </div>
              </PopperTooltip>
              {displayUid && <p className={"mt-1"}>{displayUid}</p>}
            </div>
            {showEdit && <Icon className={"icon-margin-l flex-none"} type={IconType.Pencil} />}
          </div>
        </FormControl>
      </MenuItemNavigation>
      <MenuSpacer fullwidth />
    </Fragment>
  );
};

export const extractDisplayLabels = (user: User, t: WithTranslation["t"]) => {
  const isSalesman = userIsSalesman();
  const isRegistered = userIsRegistered();
  let name, displayUid;
  const makeName = ({ firstName, lastName }: User) => {
    return [firstName, lastName].filter(Boolean).join(" ") || "MISSING NAME";
  };

  if (isSalesman) {
    const isPersonal = !user.selectedAccount || !user.selectedAccount.displayUid;
    name = isPersonal ? t("header.salesman.personal") : user.selectedAccount!.name;
    displayUid = isPersonal ? "" : user.selectedAccount!.displayUid;
  } else if (isRegistered) {
    name = makeName(user);
    displayUid = "";
  } else {
    name = makeName(user);
    displayUid = user.selectedAccount?.displayUid ?? "MISSING UID";
  }

  const displayAccount = user.selectedAccount?.name ?? "MISSING ACCOUNT";
  const displayEndCustomerAccount = user.selectedAccount?.endCustomerName ?? "";
  const displayEndCustomerUid = user.selectedAccount?.endCustomerCode ?? "";

  return {
    displayUid,
    displayName: name,
    onlyName: isRegistered,
    displayAccount,
    displayEndCustomerAccount,
    displayEndCustomerUid,
  };
};
